import { Button, FormText, TooltipShortcut } from "@itk/mui-components";
import { equal, OptionalFormController, OptionalFormInclude, required, useForm } from "@itk/react-form";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { profileSettingsPageChangePasswordSectionK, useProfileSettingsPageChangePasswordSectionT } from "src/translation-hooks/profile-settings-page-change-password-section";
import { FormType } from "./change-password-types";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { changePasswordActions } from "./change-password-store";
import { hasValue } from "@itk/typescript-utils";
import { useValidationMessageTranslationsT } from "src/translation-hooks/validation-message-translations";
import { DekoxIcon } from "src/components";
import { passwordValidationCheckmarks } from "src/utils/password-utils";
import { toastActions as itkToastActions } from "src/components/itk-toastr/itk-toastr-store";

const mapStateToProps = (state: RootState) => ({
  postChangePasswordState: state.pageReducer.profileReducer.changePasswordReducer.postChangePasswordState
});

const ownActions = {
  PostChangePasswordRequest: changePasswordActions.PostChangePasswordRequest,
  addAlert: itkToastActions.addAlert
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ChangePasswordBase = (props: Props) => {
  const { PostChangePasswordRequest, postChangePasswordState, addAlert } = props;

  const theme = useTheme();

  const profileSettingsPageChangePasswordSectionT = useProfileSettingsPageChangePasswordSectionT();
  const validationMessageTranslationsT = useValidationMessageTranslationsT();

  const [isShowingOldPassword, setIsShowingOldPassword] = useState(false);
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [isShowingConfirmPassword, setIsShowingConfirmPassword] = useState(false);

  const initialState = useMemo((): FormType => {
    return {
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    };
  }, []);

  const initFormController = useMemo(
    (): OptionalFormController<FormType> => ({
      oldPassword: { validators: [required] },
      newPassword: { validators: [required] },
      confirmPassword: { validators: state => (state.newPassword.value !== state.confirmPassword.value ? [equal(profileSettingsPageChangePasswordSectionT.newPassword), required] : [required]) }
    }),
    [profileSettingsPageChangePasswordSectionT.newPassword]
  );

  const include = useMemo((): OptionalFormInclude<FormType> => ({ newPassword: true, confirmPassword: true, oldPassword: true }), []);

  const { formState, formController, getValues, validate, resetChanges } = useForm(initialState, initFormController, include);

  const handleChangePassword = () => {
    const errors = validate();
    const data = getValues();
    const isValidPassword = passwordValidationCheckmarks(data?.newPassword || "", validationMessageTranslationsT).every(x => x.checked === true);
    if (hasValue(errors) || !hasValue(data?.newPassword) || !hasValue(data?.oldPassword) || !isValidPassword) return;

    PostChangePasswordRequest({ password: data?.newPassword, oldPassword: data?.oldPassword }, isSuccess => {
      if (isSuccess) {
        addAlert(profileSettingsPageChangePasswordSectionT.passwordChangedSuccessfully, "success");
        resetChanges();
      }
    });
  };

  return (
    <Stack flex={1} spacing={10} marginTop={2}>
      <Box>
        <FormText
          autoComplete="old-password"
          formFieldState={formState.oldPassword}
          formFieldController={formController.oldPassword}
          t={profileSettingsPageChangePasswordSectionT}
          k={profileSettingsPageChangePasswordSectionK}
          field="oldPassword"
          type={isShowingOldPassword ? "text" : "password"}
          slotProps={{
            input: {
              endAdornment: isShowingOldPassword ? (
                <DekoxIcon icon="visibilityOff" sx={{ cursor: "pointer" }} onClick={() => setIsShowingOldPassword(false)} />
              ) : (
                <DekoxIcon icon="removeRedEye" sx={{ cursor: "pointer" }} onClick={() => setIsShowingOldPassword(true)} />
              )
            }
          }}
          sx={{
            minWidth: 300,
            marginBottom: 2
          }}
        />
        <FormText
          autoComplete="new-password"
          formFieldState={formState.newPassword}
          formFieldController={formController.newPassword}
          t={profileSettingsPageChangePasswordSectionT}
          k={profileSettingsPageChangePasswordSectionK}
          field="newPassword"
          type={isShowingPassword ? "text" : "password"}
          slotProps={{
            input: {
              endAdornment: isShowingPassword ? (
                <DekoxIcon icon="visibilityOff" sx={{ cursor: "pointer" }} onClick={() => setIsShowingPassword(false)} />
              ) : (
                <DekoxIcon icon="removeRedEye" sx={{ cursor: "pointer" }} onClick={() => setIsShowingPassword(true)} />
              )
            }
          }}
          sx={{
            minWidth: 300,
            marginBottom: 2
          }}
        />
        <FormText
          autoComplete="off"
          formFieldState={formState.confirmPassword}
          formFieldController={formController.confirmPassword}
          t={profileSettingsPageChangePasswordSectionT}
          k={profileSettingsPageChangePasswordSectionK}
          field="confirmPassword"
          type={isShowingConfirmPassword ? "text" : "password"}
          slotProps={{
            input: {
              endAdornment: isShowingConfirmPassword ? (
                <DekoxIcon icon="visibilityOff" sx={{ cursor: "pointer" }} onClick={() => setIsShowingConfirmPassword(false)} />
              ) : (
                <DekoxIcon icon="removeRedEye" sx={{ cursor: "pointer" }} onClick={() => setIsShowingConfirmPassword(true)} />
              )
            }
          }}
          sx={{
            minWidth: 300,
            marginBottom: 2
          }}
        />
        <Stack gap={0.8} mb={2}>
          {passwordValidationCheckmarks(formState.newPassword.value || "", validationMessageTranslationsT).map(passwordValidationCheckmark => {
            return (
              <Stack
                key={passwordValidationCheckmark.text}
                direction="row"
                alignItems="center"
                gap={1}
                color={passwordValidationCheckmark.checked ? theme.palette.primary.main : theme.palette.customColors.darkGrey}
              >
                <DekoxIcon icon="checkCircle" fontSize="small" color="inherit" />
                <Typography variant="subtitle1" fontWeight={passwordValidationCheckmark.checked ? "bold" : "normal"}>
                  {passwordValidationCheckmark.text}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <TooltipShortcut shortcut={{ action: handleChangePassword, shortCutKey: "g", modifier: "alt" }}>
          <Button
            variant="contained"
            t={profileSettingsPageChangePasswordSectionT}
            field="changePassword"
            k={profileSettingsPageChangePasswordSectionK}
            onClick={handleChangePassword}
            disabled={postChangePasswordState.loading === "is-loading"}
            loading={postChangePasswordState.loading === "is-loading"}
          />
        </TooltipShortcut>
      </Box>
    </Stack>
  );
};

export const ChangePassword = connect(mapStateToProps, ownActions)(ChangePasswordBase);
